import type { CityConfig } from "../city-template/types";

export const pasadenaConfig: CityConfig = {
  city: "Pasadena",
  state: "California",
  stateAbbr: "CA",
  slug: "pasadena",
  route: "/cities/pasadena",
  title: "Emergency Passport Pasadena, CA | Expedited & Fast Renewal Services",
  metaDescription: "Emergency passport and expedited passport services in Pasadena, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for San Gabriel Valley travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Pasadena, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport, fast passport renewal, or urgent passport help in Pasadena? We help San Gabriel Valley travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Pasadena%20California%20cityscape%20at%20golden%20hour%20with%20historic%20Colorado%20Street%20Bridge%20and%20modern%20downtown%20buildings%2C%20San%20Gabriel%20Mountains%20backdrop%20with%20warm%20sunset%20light%2C%20City%20Hall%20tower%20visible%2C%20tree-lined%20streets%20with%20palm%20trees%2C%20professional%20architectural%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20serene%20and%20elegant%20Southern%20California%20city%20environment%20with%20warm%20amber%20tones&width=1800&height=700&seq=pasadena-hero-001&orientation=landscape",
  heroImageAlt: "Pasadena California skyline at golden hour with Colorado Street Bridge and San Gabriel Mountains",
  geoPosition: "34.1478;-118.1445",
  geoPlacename: "Pasadena, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown LA — approximately 20–30 minutes from Pasadena via I-110",
  },
  nearbyCitiesZips: [
    { name: "Pasadena", zips: "91101–91110, 91114–91118, 91121, 91123–91126, 91129, 91182, 91184, 91185, 91188, 91199" },
    { name: "Altadena", zips: "91001" },
    { name: "South Pasadena", zips: "91030, 91031" },
    { name: "San Marino", zips: "91108" },
    { name: "Alhambra", zips: "91801–91803" },
    { name: "San Gabriel", zips: "91775, 91776, 91778" },
    { name: "Monrovia", zips: "91016, 91017" },
    { name: "Arcadia", zips: "91006, 91007, 91066, 91077" },
    { name: "Glendale", zips: "91201–91206, 91208, 91210, 91214" },
    { name: "La Cañada Flintridge", zips: "91011, 91012" },
    { name: "Burbank", zips: "91501–91506, 91508, 91510, 91521–91526" },
    { name: "Temple City", zips: "91780" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent San Gabriel Valley departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Los Angeles", route: "/cities/los-angeles" },
    { name: "Fast passport renewal in San Diego", route: "/cities/san-diego" },
    { name: "Emergency passport help in San Francisco", route: "/cities/san-francisco" },
    { name: "Urgent passport services in San Jose", route: "/cities/san-jose" },
    { name: "Same-day passport help in Sacramento", route: "/cities/sacramento" },
    { name: "Passport courier services in Anaheim", route: "/cities/anaheim" },
    { name: "Rush passport help near Santa Ana", route: "/cities/santa-ana" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Pasadena remotely via secure courier. We do not have a walk-in office in Pasadena — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for San Gabriel Valley travelers.",
  fpcOfficeDistance: "Serving all of the San Gabriel Valley",
  serviceAreaIntro: "From Old Town Pasadena to the Playhouse District, from Bungalow Heaven to the Linda Vista area — Fast Passport Center serves the entire Pasadena community. We regularly help travelers from Altadena, South Pasadena, San Marino, and Alhambra get their passports fast.",
  nearbyCitiesIntro: "We serve all Pasadena neighborhoods and nearby San Gabriel Valley cities including Altadena, South Pasadena, San Marino, Alhambra, San Gabriel, Arcadia, Monrovia, and Glendale. If you are in the San Gabriel Valley, we have you covered.",
  howItWorksIntro: "Most Pasadena travelers complete everything online and ship documents overnight. No need to drive downtown or wait in line at the regional agency.",
  mistakesIntro: "With Burbank Airport nearby and LAX just 30 minutes away, Pasadena travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "Pasadena and San Gabriel Valley travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about emergency passports, expedited passports, or fast passport renewal in Pasadena? Here are the most common ones we hear from San Gabriel Valley travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent San Gabriel Valley international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Jennifer M.",
      location: "Pasadena, CA",
      rating: 5,
      text: "My trip to London was in 2 days and my passport was expired. Fast Passport Center got me a new one in under 36 hours. Absolutely saved my business conference.",
      service: "Emergency Passport",
    },
    {
      name: "Richard L.",
      location: "Altadena, CA",
      rating: 5,
      text: "Needed to renew for a family cruise to Alaska. The process was smooth, they reviewed my documents, and I had my passport in 4 days. Great service for San Gabriel Valley residents.",
      service: "Passport Renewal",
    },
    {
      name: "Sarah H.",
      location: "South Pasadena, CA",
      rating: 5,
      text: "Lost my passport the week before my wedding in Bali. They walked me through the DS-64 process and got me a replacement in 48 hours. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "David K.",
      location: "San Marino, CA",
      rating: 5,
      text: "As a frequent international traveler from the San Gabriel Valley, I can't afford passport delays. Fast Passport Center has been reliable every time. Highly recommend.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "pasadena-1",
      name: "Pasadena Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "480 E Colorado Blvd",
      city: "Pasadena",
      state: "CA",
      zip: "91101",
      phone: "(626) 795-8611",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from Old Town Pasadena",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "pasadena-2",
      name: "Altadena Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2275 Lake Ave",
      city: "Altadena",
      state: "CA",
      zip: "91001",
      phone: "(626) 794-1761",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from Altadena town center",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Altadena and La Cañada Flintridge residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "pasadena-3",
      name: "Glendale Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "313 E Broadway",
      city: "Glendale",
      state: "CA",
      zip: "91206",
      phone: "(818) 243-7734",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.3 miles from downtown Glendale",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "pasadena-4",
      name: "Burbank Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "2230 N Hollywood Way",
      city: "Burbank",
      state: "CA",
      zip: "91505",
      phone: "(818) 842-6141",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "1.5 miles from Burbank Airport",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Burbank and western San Gabriel Valley residents. Call to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "pasadena-5",
      name: "Alhambra Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "50 S Granada Ave",
      city: "Alhambra",
      state: "CA",
      zip: "91801",
      phone: "(626) 281-2944",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.4 miles from downtown Alhambra",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for eastern San Gabriel Valley residents. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "pasadena-6",
      name: "Pasadena City Clerk",
      type: "City Clerk Passport Acceptance",
      address: "100 N Garfield Ave",
      city: "Pasadena",
      state: "CA",
      zip: "91101",
      phone: "(626) 744-4121",
      hours: "Mon–Thu: 7:30 AM – 5:30 PM, Fri: 8:00 AM – 5:00 PM",
      distance: "0.2 miles from Pasadena City Hall",
      website: "https://www.cityofpasadena.net/city-clerk/",
      websiteLabel: "Pasadena City Clerk",
      notes: "Major city facility with extended hours. Photo services available on-site. Accepts walk-ins but appointments recommended.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown LA", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "San Gabriel Valley photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Pasadena area acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Pasadena applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many San Gabriel Valley families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Pasadena travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Pasadena." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Pasadena address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "Can I get an emergency passport in Pasadena before international travel?",
      a: "Yes. Fast Passport Center provides emergency passport services in Pasadena for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround for most San Gabriel Valley travelers.",
    },
    {
      q: "How fast can I renew my passport in Pasadena?",
      a: "Fast Passport Center offers fast passport renewal in Pasadena in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent.",
    },
    {
      q: "What if my passport expires before my trip to Europe?",
      a: "If your passport expires soon before a Europe trip, contact Fast Passport Center immediately. Many countries require your passport to be valid for at least 3–6 months beyond your entry date. We can expedite your passport renewal in Pasadena in 24–48 hours so you don't miss your flights.",
    },
    {
      q: "Can I get a same-day passport appointment near Pasadena?",
      a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Pasadena residents.",
    },
    {
      q: "Can I expedite a child passport in Pasadena?",
      a: "Yes. Fast Passport Center expedites child passports in Pasadena for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk San Gabriel Valley families through the entire process.",
    },
    {
      q: "What should I do if I lost my passport before a trip?",
      a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Pasadena with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours.",
    },
    {
      q: "How fast can I get a passport in Pasadena?",
      a: "With Fast Passport Center's emergency courier service, Pasadena travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to visit the Los Angeles Passport Agency downtown?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Pasadena?",
      a: "Popular options include the Pasadena Main Post Office (91101), Pasadena City Clerk (91101), Altadena Post Office (91001), and Glendale Post Office (91206). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Pasadena?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Altadena, South Pasadena, or San Marino?",
      a: "We serve all of the San Gabriel Valley including Altadena, South Pasadena, San Marino, Alhambra, Arcadia, Monrovia, and Glendale. Our overnight shipping labels work from any UPS location, so your location within the valley doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Pasadena?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Pasadena?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Pasadena, Glendale, and Alhambra offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Pasadena?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Pasadena?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk San Gabriel Valley families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Pasadena", address: "1200 E Colorado Blvd, Pasadena, CA 91106", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Pasadena", address: "1865 E Washington Blvd, Pasadena, CA 91104", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Pasadena", address: "2 N Lake Ave, Pasadena, CA 91101", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Glendale", address: "633 N Brand Blvd, Glendale, CA 91203", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Alhambra", address: "300 W Main St, Alhambra, CA 91801", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Pasadena", address: "200 N Lake Ave, Pasadena, CA 91101", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Burbank", address: "2211 N San Fernando Blvd, Burbank, CA 91504", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Alhambra", address: "2207 W Commonwealth Ave, Alhambra, CA 91803", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Pasadena",
      address: "49 S Rosemead Blvd, Pasadena, CA 91107",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — Glendale",
      address: "1335 W Glenoaks Blvd, Glendale, CA 91201",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Burbank",
      address: "850 E Angeleno Ave, Burbank, CA 91501",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Alhambra",
      address: "2211 W Commonwealth Ave, Alhambra, CA 91803",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for eastern San Gabriel Valley residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "BUR",
      name: "Hollywood Burbank Airport",
      distance: "12 miles from Pasadena",
      address: "2627 N Hollywood Way, Burbank, CA 91505",
      note: "The closest airport to Pasadena. Domestic flights with some international connections via partnerships. Much easier access than LAX.",
      flightUrl: "https://www.hollywoodburbankairport.com/",
      flightLabel: "Burbank Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "30 miles from Pasadena",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-110.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "ONT",
      name: "Ontario International Airport",
      distance: "25 miles from Pasadena",
      address: "1923 E Avion St, Ontario, CA 91761",
      note: "Convenient for eastern San Gabriel Valley residents. International flights to Mexico and domestic connections.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "Ontario Airport",
      featured: false,
    },
  ],
};